/**
 * Map Menu order from CMS schema (mapCategories with itemRefs).
 * When tour.mapbox.mapCategories exists, global order = category order + itemRefs order + uncategorized.
 * Used so the template displays and draws map layers in the same order as the Map Menu in the CMS.
 */

const ITEM_TYPES = ['building', 'poi', 'numericPoi', 'polygonPoi'];

/**
 * Build ordered list of { type, id } from mapCategories (category order + itemRefs order), then uncategorized.
 * @param {Object} mapbox - tour.mapbox
 * @returns {{ ordered: Array<{type: string, id: string}>, orderIndex: Map<string, number> }}
 */
function buildGlobalOrder(mapbox) {
  const ordered = [];
  const orderIndex = new Map();

  const categories = mapbox?.mapCategories ?? [];
  if (categories.length > 0) {
    const sortedCats = [...categories].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    for (const cat of sortedCats) {
      const refs = cat.itemRefs ?? [];
      for (const ref of refs) {
        if (ref?.id && ref?.itemType && ITEM_TYPES.includes(ref.itemType)) {
          const key = `${ref.itemType}-${ref.id}`;
          ordered.push({ type: ref.itemType, id: ref.id });
        }
      }
    }
    const inCategory = new Set(ordered.map(({ type, id }) => `${type}-${id}`));

    const byTitleThenId = (a, b) => {
      const t = (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' });
      if (t !== 0) return t;
      return (a.id || '').localeCompare(b.id || '');
    };

    const includeHiddenForType = (t) => t === 'poi' || t === 'numericPoi';
    const addUncategorized = (type, list) => {
      if (!Array.isArray(list)) return;
      const uncat = list.filter((item) => item && (includeHiddenForType(type) || !item.hidden) && !inCategory.has(`${type}-${item.id}`));
      uncat.sort(byTitleThenId);
      uncat.forEach((item) => ordered.push({ type, id: item.id }));
    };

    addUncategorized('building', mapbox.buildings);
    addUncategorized('poi', mapbox.pois);
    addUncategorized('numericPoi', mapbox.numericPois);
    addUncategorized('polygonPoi', mapbox.polygonPois);
  }

  ordered.forEach(({ type, id }, i) => orderIndex.set(`${type}-${id}`, i));
  return { ordered, orderIndex };
}

const byTitleThenId = (a, b) => {
  const t = (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' });
  if (t !== 0) return t;
  return (a.id || '').localeCompare(b.id || '');
};

/**
 * Return mapbox with buildings, pois, numericPois, polygonPois sorted by global Map Menu order.
 * When mapCategories exists: order = category order + itemRefs + uncategorized (sorted by title).
 * When no categories: order = type groups (Buildings, POIs, Numbered, Areas) each sorted by title.
 * Items get an order property set to their index so map layers (which sort by order) stay correct.
 * @param {Object} mapbox - tour.mapbox
 * @returns {Object} mapbox clone with ordered arrays and order set on each item
 */
export function getOrderedMapbox(mapbox) {
  if (!mapbox) return mapbox;

  const hasCategories = (mapbox.mapCategories ?? []).length > 0;
  const { orderIndex } = buildGlobalOrder(mapbox);

  const includeHiddenForType = (t) => t === 'poi' || t === 'numericPoi';
  const sortList = (type, list) => {
    if (!Array.isArray(list)) return list;
    const filtered = list.filter((item) => item && (includeHiddenForType(type) || !item.hidden));
    if (hasCategories) {
      return filtered
        .map((item) => ({ ...item, order: orderIndex.get(`${type}-${item.id}`) ?? 9999 }))
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }
    filtered.sort(byTitleThenId);
    return filtered.map((item, i) => ({ ...item, order: i }));
  };

  return {
    ...mapbox,
    buildings: sortList('building', mapbox.buildings),
    pois: sortList('poi', mapbox.pois),
    numericPois: sortList('numericPoi', mapbox.numericPois),
    polygonPois: sortList('polygonPoi', mapbox.polygonPois),
  };
}

/**
 * Resolve itemRef to actual item from mapbox (building/poi/numericPoi/polygonPoi).
 * @param {Object} mapbox
 * @param {string} itemType
 * @param {string} id
 * @returns {Object|undefined}
 */
export function resolveItemRef(mapbox, itemType, id) {
  if (!mapbox || !id) return undefined;
  const list =
    itemType === 'building'
      ? mapbox.buildings
      : itemType === 'poi'
        ? mapbox.pois
        : itemType === 'numericPoi'
          ? mapbox.numericPois
          : itemType === 'polygonPoi'
            ? mapbox.polygonPois
            : null;
  if (!Array.isArray(list)) return undefined;
  const item = list.find((x) => x && x.id === id);
  // For poi and numericPoi, return item even when hidden (so they appear in map menu and can be selected)
  if (item && item.hidden && itemType !== 'poi' && itemType !== 'numericPoi') return undefined;
  return item ?? undefined;
}
